import React, { useState, useEffect } from "react";
import "./App.css";
import Sidebar from "./components/sidebar/Sidebar";
import Header from "./components/header/Header";
import DashboardPage from "./components/DashboardPage/DashboardPage";
import Logo from "./assets/logo.png";

// Clerk hooks
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useUser,
  RedirectToSignIn,
} from "@clerk/clerk-react";

function App() {
  // STATE: Which page is user viewing? (overview, locations, devices, reports)
  const { isSignedIn, isLoaded } = useUser();

  // All your existing state
  const [activeSection, setActiveSection] = useState("overview");

  // STATE: Which time range is selected? (today, week, month, etc)
  const [selectedTimeRange, setSelectedTimeRange] = useState("today");

  // STATE: Mobile sidebar open/close
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // HELPER: Default date range (today from midnight to now)
  const getDefaultDateRange = () => {
    const now = new Date();
    const todayMidnight = new Date(now);
    todayMidnight.setHours(0, 0, 0, 0);
    return { start: todayMidnight, end: now };
  };

  // STATE: Date range for filtering data
  const [dateRange, setDateRange] = useState(getDefaultDateRange());

  // FUNCTION: Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // FUNCTION: Close sidebar (used when clicking overlay or close button)
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // FUNCTION: When time range changes in sidebar (handles both presets and custom)
  const handleTimeRangeChange = (newDateRange, timeRangeId) => {
    setDateRange(newDateRange);
    setSelectedTimeRange(timeRangeId);
    // Close sidebar on mobile after selection
    if (window.innerWidth <= 768) {
      closeSidebar();
    }
  };

  // FUNCTION: When section changes in sidebar
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    // Close sidebar on mobile after selection
    if (window.innerWidth <= 768) {
      closeSidebar();
    }
  };

  // FUNCTION: When date range changes in header (if re-added later)
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setSelectedTimeRange("custom");
  };

  // EFFECT: Close sidebar when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show nothing or a loader until Clerk finishes loading
  if (!isLoaded) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      {/* UNAUTHENTICATED: Show sign-in screen */}
      <SignedOut>
  <div className="auth-wrapper">
    <div className="auth-background">
      <div className="auth-grid"></div>
    </div>

    <div className="auth-container">
      <div className="auth-card-glass">
        {/* Logo / Brand */}
        <div className="auth-logo">
          <img
            src={Logo}
            alt="Company Logo"
            className="auth-logo-img"
          />
        </div>

        <h1 className="auth-title">Welcome to CoTG Dashboard</h1>
        <p className="auth-subtitle">
          Sign in to access analytics and device management
        </p>

        {/* Clerk Sign-In Button */}
        <SignInButton mode="modal">
          <button className="sign-in-button-primary">
            <i className="fas fa-right-to-bracket"></i>
            Sign In to Dashboard
          </button>
        </SignInButton>

        {/* Features */}
        <div className="auth-features">
          <div className="feature">
            <i className="fas fa-shield-alt"></i>
            <span>Secure authentication</span>
          </div>
          <div className="feature">
            <i className="fas fa-bolt"></i>
            <span>Instant access</span>
          </div>
          <div className="feature">
            <i className="fas fa-chart-line"></i>
            <span>Real-time analytics</span>
          </div>
        </div>

        <footer className="auth-footer">
          <small>© 2025 CoTG • All rights reserved</small>
        </footer>
      </div>
    </div>
  </div>
</SignedOut>

      {/* AUTHENTICATED: Full dashboard */}
      <SignedIn>
        <div className="app">
          {isSidebarOpen && (
            <div className="mobile-overlay" onClick={closeSidebar}></div>
          )}

          <Sidebar
            activeSection={activeSection}
            setActiveSection={handleSectionChange}
            onTimeRangeChange={handleTimeRangeChange}
            selectedTimeRange={selectedTimeRange}
            isMobileOpen={isSidebarOpen}
            onCloseSidebar={closeSidebar}
            userButton={<UserButton afterSignOutUrl="/" />}
          />

          <div className="main-layout">
            {/* Pass user to Header so you can show UserButton */}
            <Header
              onDateRangeChange={handleDateRangeChange}
              onRefresh={() => window.location.reload()}
              onToggleSidebar={toggleSidebar}
              
            />

            <main className="main-content">
              <DashboardPage
                activeSection={activeSection}
                timeRange={selectedTimeRange}
                dateRange={dateRange}
              />
            </main>
          </div>
        </div>
      </SignedIn>
    </>
  );
}

export default App;
